import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { Namespace } from 'react-i18next';

import { Separator } from '@swyftx/aviary/atoms/Seperator';
import { Body } from '@swyftx/aviary/atoms/Typography';

import { cn } from '@shared/utils/lib/ui';

import { Indicator } from '../Indicator/Indicator';

type StepBase = {
  id: string;
  children: Array<StepBase & { hidden?: boolean }>;
  [key: string]: any;
};

type Props<TStep extends StepBase = StepBase> = {
  step: TStep;
  activeOrder: number;
  translationKey: Namespace;
  stepNumber?: number;
  currentState: string[];
  completedSteps: Record<string, boolean>;
};

function StepperItem<TStep extends StepBase = StepBase>({
  activeOrder,
  step,
  stepNumber,
  translationKey,
  currentState,
  completedSteps,
}: Props<TStep>) {
  const { t } = useTranslation(translationKey);

  const isActive = useMemo(() => currentState.includes(step.id), [currentState, step.id]);
  const isComplete = useMemo(
    () => completedSteps[step.id] || (step.children.length && step.children.every((s) => completedSteps[s.id])),
    [completedSteps, step.id, step.children],
  );

  const offsetText = stepNumber !== undefined && 'pt-6';
  const offsetGap = stepNumber === undefined && 'mt-[-8px]';
  const offsetIndicator = stepNumber === undefined && 'p-2';

  // [INFO] "Verify email", "Complete profile", not "Name & DOB"
  const isLastParent = stepNumber === 6;

  return (
    <>
      <div className={cn(offsetGap, 'grid grid-cols-[50px_1fr] gap-12')}>
        <div className={cn(offsetIndicator, 'grid grid-rows-[auto_25px] justify-items-center gap-8')}>
          <Indicator isActive={isActive} isComplete={isComplete} stepNumber={stepNumber} />
          {!isLastParent && (
            <Separator
              orientation='vertical'
              className={cn(
                'h-16',
                isComplete ? 'bg-color-background-primary' : 'border-dashed bg-color-background-neutral',
              )}
            />
          )}
        </div>
        <Body color='primary' className={cn(offsetText)} weight='emphasis'>
          {t(`steps.${step.id}.title` as unknown as TemplateStringsArray)}
        </Body>
      </div>
      {isActive &&
        step.children
          .filter((s) => !s.hidden)
          .map((s) => (
            <StepperItem
              key={s.id}
              step={s}
              activeOrder={activeOrder}
              translationKey={translationKey}
              currentState={currentState}
              completedSteps={completedSteps}
            />
          ))}
    </>
  );
}

export { StepperItem };
